import { validateMnemonic } from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/english";
import type { SearchSpaceEstimate } from "./types";

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

/**
 * A checksum-valid sample mnemonic, used only to measure how fast THIS
 * device can run validateMnemonic — never to derive anything.
 */
const CALIBRATION_SAMPLE =
  "abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon about";

let cachedChecksumRate: number | null = null;

/**
 * Untimed iterations run before the timed sample. A cold (un-JITted) sample
 * badly underestimates steady-state throughput — measured on a 1-vCPU Linux
 * box under Node 22: ~5k/s cold vs. ~19k/s after 2000 warm-up iterations
 * (which took ~150ms), with steady state higher still.
 */
const CHECKSUM_WARMUP_ITERATIONS = 2000;

/**
 * Measures actual checksum-check throughput on this device by running a
 * short, real sample of the exact operation the search will perform, then
 * extrapolating. Replaces a fixed "typical laptop" constant, which is
 * exactly the kind of hardware-dependent guess that undersells fast
 * machines and oversells slow/throttled ones (mobile, battery saver, a
 * background tab). Result is cached for the life of the page/worker.
 */
export function calibrateChecksumRate(sampleMs = 15): number {
  if (cachedChecksumRate !== null) return cachedChecksumRate;

  for (let i = 0; i < CHECKSUM_WARMUP_ITERATIONS; i++) {
    validateMnemonic(CALIBRATION_SAMPLE, wordlist);
  }

  const start = now();
  let iterations = 0;
  while (now() - start < sampleMs) {
    validateMnemonic(CALIBRATION_SAMPLE, wordlist);
    iterations++;
  }
  const elapsedSeconds = (now() - start) / 1000;
  // Floor it — a near-zero elapsed time on a very fast first pass would
  // otherwise extrapolate to an absurd, overconfident rate.
  cachedChecksumRate = Math.max(10_000, iterations / elapsedSeconds);
  return cachedChecksumRate;
}

/** Escape hatch for tests or environments where timing APIs behave oddly. */
export function resetChecksumRateCalibration(): void {
  cachedChecksumRate = null;
}

/** Above this, we refuse to run automatically and require explicit confirmation. */
export const FEASIBLE_SECONDS_THRESHOLD = 120; // ~2 minutes

export function estimateChecksumOnlySearch(totalCandidates: number): SearchSpaceEstimate {
  const rate = calibrateChecksumRate();
  const estimatedSeconds = totalCandidates / rate;
  const feasible = estimatedSeconds <= FEASIBLE_SECONDS_THRESHOLD;
  return {
    totalCandidates,
    estimatedSeconds,
    feasible,
    reason: feasible
      ? `${totalCandidates.toLocaleString()} candidates, roughly ${estimatedSeconds.toFixed(
          1
        )}s of checksum checks on this device (measured at ~${Math.round(
          rate
        ).toLocaleString()}/s).`
      : `${totalCandidates.toLocaleString()} candidates would take roughly ${formatDuration(
          estimatedSeconds
        )} of checksum checks alone on this device — before any address verification. This is not run automatically.`,
  };
}

export function estimateMissingWordsSpace(missingCount: number): SearchSpaceEstimate {
  // 2048 possibilities per missing slot; checksum filtering happens per-candidate, cheaply.
  const totalCandidates = Math.pow(2048, missingCount);
  return estimateChecksumOnlySearch(totalCandidates);
}

export function estimateWordOrderSpace(positionsInPlay: number): SearchSpaceEstimate {
  const totalCandidates = factorial(positionsInPlay);
  return estimateChecksumOnlySearch(totalCandidates);
}

function factorial(n: number): number {
  let result = 1;
  for (let i = 2; i <= n; i++) result *= i;
  return result;
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(0)}s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)} minutes`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)} hours`;
  if (seconds < 31_536_000) return `${(seconds / 86400).toFixed(1)} days`;
  return `${(seconds / 31_536_000).toExponential(2)} years`;
}
